import React from 'react';
import { Plus, Trash2 } from 'lucide-react';

/**
 * LoanDataForm - Form component for collecting loan data during BA job creation
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.7, 2.8
 */

export interface LoanDataFormState {
  borrowerName: string;
  loanNumber: string;
  propertyAddress: string;
  loanAmount: string;
  coBorrowerNames: string[];
}

interface LoanDataFormProps {
  formState: LoanDataFormState;
  onChange: (state: LoanDataFormState) => void;
  validationError: string | null;
}

const LoanDataForm: React.FC<LoanDataFormProps> = ({
  formState,
  onChange,
  validationError,
}) => {
  const handleBorrowerNameChange = (value: string) => {
    onChange({ ...formState, borrowerName: value });
  };

  const handleLoanNumberChange = (value: string) => {
    onChange({ ...formState, loanNumber: value });
  };

  const handlePropertyAddressChange = (value: string) => {
    onChange({ ...formState, propertyAddress: value });
  };

  const handleLoanAmountChange = (value: string) => {
    onChange({ ...formState, loanAmount: value });
  };

  /**
   * Add a new co-borrower field
   * Requirement 2.2: "Add Co-Borrower" button appends a new co-borrower name input field
   * Requirement 2.4: No maximum limit on co-borrowers
   */
  const handleAddCoBorrower = () => {
    onChange({
      ...formState,
      coBorrowerNames: [...formState.coBorrowerNames, ''],
    });
  };

  /**
   * Remove a co-borrower field by index
   * Requirement 2.3: Allow user to remove any co-borrower field
   */
  const handleRemoveCoBorrower = (index: number) => {
    onChange({
      ...formState,
      coBorrowerNames: formState.coBorrowerNames.filter((_, i) => i !== index),
    });
  };

  const handleCoBorrowerNameChange = (index: number, value: string) => {
    const updated = [...formState.coBorrowerNames];
    updated[index] = value;
    onChange({ ...formState, coBorrowerNames: updated });
  };

  return (
    <div className="space-y-4">
      {/* Borrower Name - Required (Requirement 2.1, 2.7) */}
      <div>
        <label
          htmlFor="borrowerName"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Borrower Name <span className="text-red-500">*</span>
        </label>
        <input
          id="borrowerName"
          type="text"
          value={formState.borrowerName}
          onChange={(e) => handleBorrowerNameChange(e.target.value)}
          placeholder="Enter primary borrower name"
          className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            validationError
              ? 'border-red-300'
              : 'border-gray-300'
          }`}
        />
        {validationError && (
          <p className="mt-1 text-xs text-red-600">
            {validationError}
          </p>
        )}
      </div>

      {/* Loan Number - Optional (Requirement 2.8) */}
      <div>
        <label
          htmlFor="loanNumber"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Loan Number
        </label>
        <input
          id="loanNumber"
          type="text"
          value={formState.loanNumber}
          onChange={(e) => handleLoanNumberChange(e.target.value)}
          placeholder="Enter loan number (optional)"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Property Address - Optional (Requirement 2.8) */}
      <div>
        <label
          htmlFor="propertyAddress"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Property Address
        </label>
        <input
          id="propertyAddress"
          type="text"
          value={formState.propertyAddress}
          onChange={(e) => handlePropertyAddressChange(e.target.value)}
          placeholder="Enter property address (optional)"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Loan Amount - Optional */}
      <div>
        <label
          htmlFor="loanAmount"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Loan Amount
        </label>
        <input
          id="loanAmount"
          type="text"
          value={formState.loanAmount}
          onChange={(e) => handleLoanAmountChange(e.target.value)}
          placeholder="Enter loan amount (optional)"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Co-Borrowers - Dynamic fields (Requirements 2.2, 2.3, 2.4) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Co-Borrowers
          </label>
          <button
            type="button"
            onClick={handleAddCoBorrower}
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Co-Borrower
          </button>
        </div>

        {formState.coBorrowerNames.length === 0 && (
          <p className="text-xs text-gray-500">
            No co-borrowers added. Click "Add Co-Borrower" to add one.
          </p>
        )}

        <div className="space-y-2">
          {formState.coBorrowerNames.map((name, index) => (
            <div key={index} className="flex items-center space-x-2">
              <input
                type="text"
                value={name}
                onChange={(e) => handleCoBorrowerNameChange(index, e.target.value)}
                placeholder={`Co-borrower ${index + 1} name`}
                aria-label={`Co-borrower ${index + 1} name`}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => handleRemoveCoBorrower(index)}
                className="inline-flex items-center px-2 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                aria-label={`Remove co-borrower ${index + 1}`}
              >
                <Trash2 className="h-4 w-4" />
                <span className="ml-1 text-sm">Remove</span>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoanDataForm;

/**
 * Validates the loan data form state.
 * Requirement 2.7: Borrower name must not be empty/whitespace-only
 *
 * @returns validation error message or null if valid
 */
export function validateLoanDataForm(formState: LoanDataFormState): string | null {
  if (!formState.borrowerName.trim()) {
    return 'Borrower name is required';
  }
  return null;
}
